import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { ActiveAlert } from './TelemetryContext';
import { usePlan, PlanId } from './PlanContext';

// ── Types ──────────────────────────────────────────────────────

export type TicketStatus    = 'open' | 'assigned' | 'in_progress' | 'resolved' | 'closed';
export type TicketSeverity  = 'warning' | 'critical';
export type TechnicianRole  = 'admin' | 'operator' | 'technician';

export interface Technician {
  id:    string;
  name:  string;
  email: string;
  role:  TechnicianRole;
  avatar: string; // initials
}

export interface TicketEvent {
  ts:      number;
  actor:   string;
  action:  string;
  note?:   string;
}

export interface MaintenanceTicket {
  ticketId:        string;
  workspaceId:     string;
  title:           string;
  description:     string;
  severity:        TicketSeverity;
  status:          TicketStatus;
  machineId:       string;
  machineName:     string;
  sensorId?:       string;
  sensorName?:     string;
  alertId?:        string;
  assigneeId?:     string;
  assigneeName?:   string;
  createdAt:       number;
  updatedAt:       number;
  resolvedAt?:     number;
  slaDeadline:     number;   // ms timestamp
  escalatedAt?:    number;
  resolutionNotes?: string;
  timeline:        TicketEvent[];
}

// SLA windows in ms by plan + severity
const SLA_MS: Record<PlanId, Record<TicketSeverity, number>> = {
  free:        { critical: 4 * 3600_000,  warning: 24 * 3600_000 },
  subscriber:  { critical: 2 * 3600_000,  warning:  8 * 3600_000 },
  enterprise:  { critical: 1 * 3600_000,  warning:  4 * 3600_000 },
};

// ── Seed technicians ───────────────────────────────────────────

export const SEED_TECHNICIANS: Technician[] = [
  { id: 'tech_001', name: 'Ravi Kumar',    email: 'ravi@cookiefactory.com',    role: 'technician', avatar: 'RK' },
  { id: 'tech_002', name: 'Priya Sharma',  email: 'priya@cookiefactory.com',   role: 'operator',   avatar: 'PS' },
  { id: 'tech_003', name: 'Admin User',    email: 'admin@cookiefactory.com',   role: 'admin',      avatar: 'AU' },
  { id: 'tech_004', name: 'Jyothika R',    email: 'jyothika@cookiefactory.com',role: 'operator',   avatar: 'JR' },
];

// ── Seed tickets ───────────────────────────────────────────────

const now = Date.now();

const SEED_TICKETS: MaintenanceTicket[] = [
  {
    ticketId: 'ticket_001', workspaceId: 'demo',
    title: 'Motor Temp exceeding threshold on Mixer Alpha',
    description: 'Temperature sensor reading 91°C, above max threshold of 85°C. Immediate inspection required.',
    severity: 'critical', status: 'in_progress',
    machineId: 'machine_001', machineName: 'Mixer Alpha',
    sensorId: 's_001', sensorName: 'Motor Temp',
    assigneeId: 'tech_001', assigneeName: 'Ravi Kumar',
    createdAt: now - 3600_000, updatedAt: now - 1800_000,
    slaDeadline: now - 3600_000 + SLA_MS.free.critical,
    timeline: [
      { ts: now - 3600_000, actor: 'System',     action: 'Ticket auto-created from critical alert' },
      { ts: now - 3000_000, actor: 'Admin User',  action: 'Assigned to Ravi Kumar' },
      { ts: now - 1800_000, actor: 'Ravi Kumar',  action: 'Status changed to In Progress', note: 'Inspecting motor cooling system' },
    ],
  },
  {
    ticketId: 'ticket_002', workspaceId: 'demo',
    title: 'Vibration anomaly — Mixer Alpha Shaft',
    description: 'Vibration sensor reading 2.8g, above max threshold of 2.5g. Possible bearing wear.',
    severity: 'warning', status: 'open',
    machineId: 'machine_001', machineName: 'Mixer Alpha',
    sensorId: 's_003', sensorName: 'Vibration',
    createdAt: now - 7200_000, updatedAt: now - 7200_000,
    slaDeadline: now - 7200_000 + SLA_MS.free.warning,
    timeline: [
      { ts: now - 7200_000, actor: 'System', action: 'Ticket auto-created from warning alert' },
    ],
  },
  {
    ticketId: 'ticket_003', workspaceId: 'demo',
    title: 'Conveyor Belt B2 — Belt Speed zero during active shift',
    description: 'Belt speed dropped to 0 RPM during scheduled production window.',
    severity: 'warning', status: 'assigned',
    machineId: 'machine_002', machineName: 'Conveyor Belt B2',
    sensorId: 's_004', sensorName: 'Belt Speed',
    assigneeId: 'tech_002', assigneeName: 'Priya Sharma',
    createdAt: now - 10800_000, updatedAt: now - 9000_000,
    slaDeadline: now - 10800_000 + SLA_MS.free.warning,
    timeline: [
      { ts: now - 10800_000, actor: 'System',       action: 'Ticket auto-created from warning alert' },
      { ts: now -  9000_000, actor: 'Admin User',    action: 'Assigned to Priya Sharma' },
    ],
  },
  {
    ticketId: 'ticket_004', workspaceId: 'demo',
    title: 'Packaging Unit P1 — Air Pressure low',
    description: 'Air pressure dropped to 3.8 bar, below minimum threshold of 4 bar.',
    severity: 'critical', status: 'resolved',
    machineId: 'machine_003', machineName: 'Packaging Unit P1',
    sensorId: 's_006', sensorName: 'Air Pressure',
    assigneeId: 'tech_001', assigneeName: 'Ravi Kumar',
    createdAt: now - 86400_000, updatedAt: now - 82800_000,
    resolvedAt: now - 82800_000,
    slaDeadline: now - 86400_000 + SLA_MS.free.critical,
    resolutionNotes: 'Replaced faulty pressure regulator valve. System back to normal operating pressure.',
    timeline: [
      { ts: now - 86400_000, actor: 'System',     action: 'Ticket auto-created from critical alert' },
      { ts: now - 85000_000, actor: 'Admin User',  action: 'Assigned to Ravi Kumar' },
      { ts: now - 84000_000, actor: 'Ravi Kumar',  action: 'Status changed to In Progress' },
      { ts: now - 82800_000, actor: 'Ravi Kumar',  action: 'Resolved', note: 'Replaced faulty pressure regulator valve.' },
    ],
  },
];

// ── Context ────────────────────────────────────────────────────

interface TicketCtx {
  tickets:       MaintenanceTicket[];
  technicians:   Technician[];
  createTicket:  (t: Omit<MaintenanceTicket, 'ticketId' | 'createdAt' | 'updatedAt' | 'timeline' | 'slaDeadline'>) => MaintenanceTicket;
  createFromAlert: (alert: ActiveAlert) => MaintenanceTicket | null;
  assignTicket:  (ticketId: string, techId: string, techName: string) => void;
  updateStatus:  (ticketId: string, status: TicketStatus, actor: string, note?: string) => void;
  addNote:       (ticketId: string, actor: string, note: string) => void;
  resolveTicket: (ticketId: string, actor: string, notes: string) => void;
  closeTicket:   (ticketId: string, actor: string) => void;
  openCount:     number;
  overdueCount:  number;
}

const TicketContext = createContext<TicketCtx>({} as TicketCtx);

// ── Provider ───────────────────────────────────────────────────

export const TicketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tickets, setTickets] = useState<MaintenanceTicket[]>(SEED_TICKETS);
  const { plan } = usePlan();
  // Track which sensorIds already have an open ticket (dedup)
  const openSensors = useRef<Set<string>>(new Set(
    SEED_TICKETS.filter(t => !['resolved','closed'].includes(t.status) && t.sensorId)
      .map(t => t.sensorId!)
  ));

  const slaWindow = useCallback((severity: TicketSeverity): number => {
    return SLA_MS[plan.id as PlanId]?.[severity] ?? SLA_MS.free[severity];
  }, [plan.id]);

  const createTicket = useCallback((
    t: Omit<MaintenanceTicket, 'ticketId' | 'createdAt' | 'updatedAt' | 'timeline' | 'slaDeadline'>
  ): MaintenanceTicket => {
    const now = Date.now();
    const ticket: MaintenanceTicket = {
      ...t,
      ticketId:  `ticket_${now}`,
      createdAt: now,
      updatedAt: now,
      slaDeadline: now + slaWindow(t.severity),
      timeline: [{ ts: now, actor: 'System', action: 'Ticket created' }],
    };
    setTickets(prev => [ticket, ...prev]);
    if (t.sensorId) openSensors.current.add(t.sensorId);
    return ticket;
  }, [slaWindow]);

  // Auto-create from alert — deduplicates by sensorId
  const createFromAlert = useCallback((alert: ActiveAlert): MaintenanceTicket | null => {
    if (alert.sensorId && openSensors.current.has(alert.sensorId)) return null;
    const severity: TicketSeverity = alert.severity === 'CRITICAL' ? 'critical' : 'warning';
    const direction = alert.direction === 'high' ? 'above' : 'below';
    return createTicket({
      workspaceId:  'demo',
      title:        `${alert.sensorName} ${direction} threshold on ${alert.machineName}`,
      description:  `${alert.sensorName} reading ${alert.value.toFixed(1)} — ${direction} threshold of ${alert.threshold}. Auto-generated from telemetry alert.`,
      severity,
      status:       'open',
      machineId:    alert.machineId,
      machineName:  alert.machineName,
      sensorId:     alert.sensorId,
      sensorName:   alert.sensorName,
      alertId:      alert.id,
    });
  }, [createTicket]);

  const mutate = useCallback((ticketId: string, fn: (t: MaintenanceTicket) => MaintenanceTicket) => {
    setTickets(prev => prev.map(t => t.ticketId === ticketId ? fn(t) : t));
  }, []);

  const assignTicket = useCallback((ticketId: string, techId: string, techName: string) => {
    mutate(ticketId, t => ({
      ...t,
      assigneeId:   techId,
      assigneeName: techName,
      status:       t.status === 'open' ? 'assigned' : t.status,
      updatedAt:    Date.now(),
      timeline:     [...t.timeline, { ts: Date.now(), actor: 'System', action: `Assigned to ${techName}` }],
    }));
  }, [mutate]);

  const updateStatus = useCallback((ticketId: string, status: TicketStatus, actor: string, note?: string) => {
    mutate(ticketId, t => ({
      ...t, status, updatedAt: Date.now(),
      timeline: [...t.timeline, { ts: Date.now(), actor, action: `Status → ${status}`, note }],
    }));
  }, [mutate]);

  const addNote = useCallback((ticketId: string, actor: string, note: string) => {
    mutate(ticketId, t => ({
      ...t, updatedAt: Date.now(),
      timeline: [...t.timeline, { ts: Date.now(), actor, action: 'Note added', note }],
    }));
  }, [mutate]);

  const resolveTicket = useCallback((ticketId: string, actor: string, notes: string) => {
    const now = Date.now();
    mutate(ticketId, t => {
      if (t.sensorId) openSensors.current.delete(t.sensorId);
      return {
        ...t, status: 'resolved', resolvedAt: now, updatedAt: now,
        resolutionNotes: notes,
        timeline: [...t.timeline, { ts: now, actor, action: 'Resolved', note: notes }],
      };
    });
  }, [mutate]);

  const closeTicket = useCallback((ticketId: string, actor: string) => {
    const now = Date.now();
    mutate(ticketId, t => ({
      ...t, status: 'closed', updatedAt: now,
      timeline: [...t.timeline, { ts: now, actor, action: 'Ticket closed' }],
    }));
  }, [mutate]);

  // SLA escalation scanner — runs every 60s
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setTickets(prev => prev.map(t => {
        if (['resolved','closed'].includes(t.status)) return t;
        if (t.escalatedAt) return t;
        if (now > t.slaDeadline) {
          return {
            ...t, escalatedAt: now, updatedAt: now,
            timeline: [...t.timeline, { ts: now, actor: 'System', action: '⚠️ SLA breached — escalated to Admin' }],
          };
        }
        return t;
      }));
    }, 60_000);
    return () => clearInterval(iv);
  }, []);

  const openCount   = tickets.filter(t => !['resolved','closed'].includes(t.status)).length;
  const overdueCount = tickets.filter(t =>
    !['resolved','closed'].includes(t.status) && Date.now() > t.slaDeadline
  ).length;

  return (
    <TicketContext.Provider value={{
      tickets, technicians: SEED_TECHNICIANS,
      createTicket, createFromAlert, assignTicket,
      updateStatus, addNote, resolveTicket, closeTicket,
      openCount, overdueCount,
    }}>
      {children}
    </TicketContext.Provider>
  );
};

export const useTickets = () => useContext(TicketContext);
